import { useEffect } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Spinner } from '../../lib/shadcn/spinner'
import { Chip } from '../interviews/Chip'
import { useGetDriverDrivingReport } from '../../hooks/backend/drivers'
import { readableDate } from '../../utils/coachingDates'
import { CONSENT_LABEL, fineSpeedLine, fineStanding, rand, type FineRow } from '../../utils/drivingFines'
import { KIND_LABEL, STEP_LABEL, dayLabel, seconds, speedLine, standingLabel, stepTone, type DrivingEvent, type Step } from '../../utils/drivingConduct'

// The Driving tab on a driver record: the clause 21.7 weekly event reports as
// they were written each Monday (driving_weekly_report), this week so far, and
// where the driver stands on the ladder. All of it from
// driving_driver_report_v1 (db/migrations/0374); nothing is worked out here.
//
// A report shows the same rows the driver's email carried, because both are
// the one row written on the Monday. "Held" means it was written while sending
// was switched off, so the driver has not been emailed it.

type Report = {
  week_start: string
  week_end: string
  shifts: number
  speeding: number
  reckless: number
  harsh_braking: number
  conduct_applies: boolean
  status: 'queued' | 'held_switch_off' | 'no_email'
  events: DrivingEvent[]
  sent_at: string | null
}

type DriverReport = {
  driver: boolean
  position: {
    conduct_applies: boolean
    gate_date: string | null
    gate_version: string | null
    verbal_lapses_on: string | null
    final_lapses_on: string | null
    next_breach_week_step: Step
    open_cases: number
    fines_awaiting_response: number
    fines_disputed: number
    fines_counted: number
    fines_reckless: number
  } | null
  this_week: { week_start: string; week_end: string; events: DrivingEvent[] }
  reports: Report[]
  // Speeding fines on the ladder (clause 21.6A, 0484).
  fines: (Pick<FineRow, 'fine_id' | 'notice_number' | 'plate' | 'offence_on' | 'offence_time' | 'place' | 'recorded_kmh' | 'limit_kmh' | 'amount_zar' | 'reckless' | 'bucket' | 'stage' | 'outcome' | 'response_due_on' | 'coaching_only_reason'> & {
    consent_status: keyof typeof CONSENT_LABEL | null
    case_status: string | null
  })[]
}

const REPORT_STATUS: Record<Report['status'], string> = {
  queued: 'Emailed',
  held_switch_off: 'Held: sending is switched off',
  no_email: 'Not emailed: no address on record',
}

function EventRows({ events }: { events: DrivingEvent[] }) {
  if (events.length === 0) return <p className="text-xs text-muted-foreground">No events.</p>
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-xs">
        <thead className="bg-muted text-left text-muted-foreground">
          <tr>
            <th className="px-2 py-1.5">When</th>
            <th className="px-2 py-1.5">What</th>
            <th className="px-2 py-1.5">Where</th>
            <th className="px-2 py-1.5">Speed</th>
            <th className="px-2 py-1.5">For</th>
            <th className="px-2 py-1.5">Standing</th>
          </tr>
        </thead>
        <tbody>
          {events.map((e) => (
            <tr key={e.event_id} className="border-t border-border">
              <td className="whitespace-nowrap px-2 py-1.5">
                {dayLabel(e.event_date)} {String(e.event_time).slice(0, 5)}
              </td>
              <td className="px-2 py-1.5">{KIND_LABEL[e.kind] ?? e.kind}</td>
              <td className="px-2 py-1.5">{e.place || '—'}</td>
              <td className="whitespace-nowrap px-2 py-1.5">{speedLine(e)}</td>
              <td className="whitespace-nowrap px-2 py-1.5">{seconds(e.duration_s)}</td>
              <td className="px-2 py-1.5 text-muted-foreground">{standingLabel(e)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export function DrivingReportPanel({ applicationId }: { applicationId: string }) {
  const report = useGetDriverDrivingReport<DriverReport>()
  const load = report.trigger
  useEffect(() => {
    load({ application_id: applicationId }, { skipCache: true })
  }, [applicationId, load])

  if (report.loading && !report.data) {
    return (
      <div className="flex justify-center py-6">
        <Spinner />
      </div>
    )
  }
  if (report.error) {
    return (
      <p className="flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        {report.error}
      </p>
    )
  }
  const r = report.data
  if (!r) return null
  if (!r.driver) return <p className="text-xs text-muted-foreground">This applicant is not a driver yet, so there is no driving to show.</p>

  const p = r.position
  return (
    <div className="space-y-4 text-sm">
      {p && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Chip tone={p.conduct_applies ? 'brand' : 'neutral'}>
            {p.conduct_applies
              ? `Clauses 21.5 to 21.7 apply from ${readableDate(p.gate_date)}`
              : 'Coaching only: no signed v6 or Addendum No. 1'}
          </Chip>
          {p.verbal_lapses_on && <Chip tone="amber">Verbal warning until {readableDate(p.verbal_lapses_on)}</Chip>}
          {p.final_lapses_on && <Chip tone="red">Final written warning until {readableDate(p.final_lapses_on)}</Chip>}
          <Chip tone={stepTone(p.next_breach_week_step)}>Next breach week: {STEP_LABEL[p.next_breach_week_step]}</Chip>
          {p.open_cases > 0 && <Chip tone="amber">{p.open_cases} open case(s) on the Driving conduct page</Chip>}
          {p.fines_awaiting_response + p.fines_disputed > 0 && (
            <Chip tone="amber">{p.fines_awaiting_response + p.fines_disputed} speeding fine(s) in the dispute step</Chip>
          )}
          {p.fines_counted > 0 && <Chip tone="amber">{p.fines_counted} speeding fine(s) counted</Chip>}
          {p.fines_reckless > 0 && <Chip tone="red">{p.fines_reckless} reckless speeding fine(s)</Chip>}
        </div>
      )}

      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
          This week so far, {dayLabel(r.this_week.week_start)} to {dayLabel(r.this_week.week_end)}
        </h4>
        <EventRows events={r.this_week.events} />
      </div>

      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Speeding fines</h4>
        {(r.fines ?? []).length === 0 ? (
          <p className="text-xs text-muted-foreground">No speeding fine is attributed to this driver.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-xs">
              <thead className="bg-muted text-left text-muted-foreground">
                <tr>
                  <th className="px-2 py-1.5">When</th>
                  <th className="px-2 py-1.5">Car and notice</th>
                  <th className="px-2 py-1.5">Speed</th>
                  <th className="px-2 py-1.5">Amount</th>
                  <th className="px-2 py-1.5">Standing</th>
                </tr>
              </thead>
              <tbody>
                {r.fines.map((f) => (
                  <tr key={f.fine_id} className="border-t border-border">
                    <td className="whitespace-nowrap px-2 py-1.5">
                      {f.offence_on ? `${dayLabel(f.offence_on)} ${f.offence_time ?? ''}` : '—'}
                    </td>
                    <td className="px-2 py-1.5">
                      {f.plate ?? '—'}, {f.notice_number ?? 'no notice number'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">
                      {fineSpeedLine({ ...f, over_limit_kmh: null })}
                      {f.reckless && <span className="ml-1 text-red-700">reckless</span>}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5">{rand(f.amount_zar)}</td>
                    <td className="px-2 py-1.5 text-muted-foreground">
                      {fineStanding({ ...f, unattributed_reason: null, conduct_eligible: !f.coaching_only_reason, counted_in: null, case_status: f.case_status })}
                      {f.consent_status && <div>{CONSENT_LABEL[f.consent_status]}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h4 className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Weekly reports</h4>
        {r.reports.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            No weekly report has been written yet. The first is written on the Monday after a week with at least one
            shift.
          </p>
        ) : (
          <div className="space-y-2">
            {r.reports.map((w) => (
              <details key={w.week_start} className="rounded-md border border-border p-2">
                <summary className="cursor-pointer text-xs">
                  <span className="font-medium">
                    {dayLabel(w.week_start)} to {dayLabel(w.week_end)}
                  </span>
                  : {w.shifts} shift(s), {w.speeding} speeding, {w.harsh_braking} harsh braking
                  {w.reckless > 0 && `, ${w.reckless} reckless`}.{' '}
                  <span className="text-muted-foreground">{REPORT_STATUS[w.status]}</span>
                </summary>
                <div className="mt-2">
                  <EventRows events={w.events} />
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
